import type React from 'react';

import type { ColorName } from 'styles/config/colors';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';

type SVGProps = React.SVGProps<SVGSVGElement>;
type CustomSVGProps = Omit<SVGProps, 'height' | 'width' | 'stroke' | 'fill'>;

export enum IconSize {
  xxs = 5,
  xs = 12,
  sm = 16,
  md = 20,
  lg = 24,
  xl = 32,
  xxl = 40,
  xxxl = 48,
}

export interface IconProps extends CustomSVGProps {
  size?: keyof typeof IconSize;
  color?: ColorName;
  customFill?: boolean;
  className?: string;
  viewboxSize?: keyof typeof IconSize;
  preserveColor?: boolean;
}

export const BaseIcon: ComponentWithChildren<IconProps> = ({
  children,
  size = 'md',
  color,
  customFill = false,
  className,
  viewboxSize = 'xl',
  style,
  ...rest
}) => {
  const svgStyle: React.CSSProperties = {
    ...(!customFill
      ? { fill: 'currentColor', stroke: 'none' }
      : { stroke: 'none' }),
    ...style,
  };

  return (
    <span
      className={cn('[display:contents]', className)}
      style={color ? { color: colorCssVar(color) } : undefined}
    >
      <svg
        fill="none"
        height={IconSize[size]}
        viewBox={`0 0 ${IconSize[viewboxSize]} ${IconSize[viewboxSize]}`}
        width={IconSize[size]}
        xmlns="http://www.w3.org/2000/svg"
        style={svgStyle}
        {...rest}
      >
        {children}
      </svg>
    </span>
  );
};
